import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Gift, Users, Package, TrendingUp, Clock, Shield, Truck, Award, CheckCircle } from 'lucide-react';

const Home = () => {
  const services = [
    {
      icon: <Gift className="w-8 h-8" />,
      title: 'Corporate Gifting',
      description: 'Curated gift solutions that reflect your brand values and strengthen client relationships.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Employee Welcome Kits',
      description: 'Make first impressions count with professionally designed onboarding kits for new hires.'
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Promotional Merchandise',
      description: 'Custom branded products that amplify your marketing efforts and brand visibility.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Event & Conference Giveaways',
      description: 'Memorable event merchandise that leaves a lasting impression on attendees.'
    },
    {
      icon: <Gift className="w-8 h-8" />,
      title: 'Festive Gifting Solutions',
      description: 'Seasonal and festive corporate gifts that celebrate special occasions with elegance.'
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Custom Branded Merchandise',
      description: 'End-to-end branding and customization services for all your merchandise needs.'
    },
  ];

  const features = [
    { icon: <Award />, title: 'Premium Curated Products', description: 'Handpicked selection of quality items' },
    { icon: <Gift />, title: 'Custom Branding & Packaging', description: 'Tailored solutions for your brand' },
    { icon: <TrendingUp />, title: 'Competitive Pricing', description: 'Best value for bulk orders' },
    { icon: <Truck />, title: 'Pan-India Delivery', description: 'Reliable logistics nationwide' },
    { icon: <Clock />, title: 'Fast Turnaround', description: 'Quick order fulfillment' },
    { icon: <Shield />, title: 'Quality Assurance', description: 'Rigorous quality checks' },
  ];

  return (
    <div>
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-surface via-background to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
            >
              <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="hero-label">
                Premium B2B Solutions
              </div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[0.95] mb-6" data-testid="hero-headline">
                Elevating Brands Through{' '}
                <span className="text-accent">Innovative Merchandise</span>
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-muted max-w-2xl mb-8" data-testid="hero-subheadline">
                Premium corporate gifting & promotional merchandise solutions for modern businesses.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/products"
                  data-testid="hero-cta-explore"
                  className="h-12 px-8 rounded-full bg-accent text-white font-medium hover:scale-105 transition-transform duration-200 shadow-xl shadow-accent/20 flex items-center justify-center"
                >
                  Explore Products <ArrowRight className="ml-2" size={20} />
                </Link>
                <a
                  href="#catalogue"
                  data-testid="hero-cta-catalogue"
                  className="h-12 px-8 rounded-full bg-primary text-white font-medium hover:bg-primary/90 transition-colors flex items-center justify-center"
                >
                  Request Catalogue
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <img
                src="https://images.unsplash.com/photo-1607614564906-234871d9608f?auto=format&fit=crop&q=80"
                alt="Corporate Gift Hampers"
                className="rounded-2xl shadow-2xl w-full"
                data-testid="hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="services-label">
              What We Offer
            </div>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="services-title">
              Our Services
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto" data-testid="services-description">
              Comprehensive corporate gifting and merchandise solutions tailored for your business needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative overflow-hidden bg-white border border-slate-100 p-8 rounded-xl hover:border-accent/20 hover:shadow-lg transition-all duration-300"
                data-testid={`service-card-${index}`}
              >
                <div className="text-accent mb-4" data-testid={`service-icon-${index}`}>{service.icon}</div>
                <h3 className="text-xl font-semibold mb-3" data-testid={`service-title-${index}`}>{service.title}</h3>
                <p className="text-muted text-sm leading-relaxed" data-testid={`service-description-${index}`}>{service.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="services-view-all-link"
              className="inline-flex items-center text-accent font-medium hover:underline"
            >
              View All Services <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="why-choose-label">
              Why Partner With Us
            </div>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="why-choose-title">
              Why Choose SwiftHampers
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-start space-x-4"
                data-testid={`feature-${index}`}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1" data-testid={`feature-title-${index}`}>{feature.title}</h3>
                  <p className="text-sm text-muted" data-testid={`feature-description-${index}`}>{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="catalogue" className="py-24 md:py-32 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-accent to-transparent"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-testid="cta-title">
              Looking for Corporate Gifts or Merchandise?
            </h2>
            <p className="text-lg text-gray-300 mb-8" data-testid="cta-description">
              Get in touch with us today and discover how we can help elevate your brand through premium gifting solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-request-quote"
                className="h-12 px-8 rounded-full bg-accent text-white font-medium hover:scale-105 transition-transform duration-200 shadow-xl shadow-accent/20 flex items-center justify-center"
              >
                Request a Quote
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/catalogue/download`}
                data-testid="cta-download-catalogue"
                className="h-12 px-8 rounded-full bg-white text-primary font-medium hover:bg-gray-100 transition-colors flex items-center justify-center"
              >
                Download Catalogue
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
